import {
  ColumnDef,
  functionalUpdate,
  PaginationState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import {
  ReportMetricsResponse,
  useReportMetricsContextV2,
} from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { TableProps, VirtuosoScrollEvent } from 'src/tables/Table';
import { Feature } from 'src/WebApiController';

import { ReportTableV2Props } from './ReportTableCommon.types';

export const useReportDetailsTableV2 = ({
  metricsData,
  summaryData,
  report,
  onMount,
  onUnmount,
  initState,
  className,
  displayedColumnsConfig,
}: ReportTableV2Props & {
  displayedColumnsConfig: ColumnDef<ReportMetricsResponse | null>[];
}) => {
  const {
    isFetchingNextPage,
    updateTempReportConfig,
    fetchNextPage,
    hasNextPage,
    isReportConfigEmpty,
  } = useReportMetricsContextV2();

  // Enable passing in table state as parameters -- we can remount with the last state the user was on
  const { activeSortTableColumn, setActiveSortTableColumn } =
    useActiveSortTableColumnContext();

  const sortingStateFromReportConfig: SortingState | null = useMemo(() => {
    if (report.request.orderBy) {
      return [
        {
          id: report.request.orderBy,
          desc: !report.request.orderByAsc,
        },
      ];
    }
    return null;
  }, [report.request.orderBy, report.request.orderByAsc]);

  const [sorting, setSorting] = useState<SortingState>(
    sortingStateFromReportConfig ||
      activeSortTableColumn ||
      initState?.sorting ||
      []
  );

  const hasTableFooterColumnVirtualizedFeature = useUserHasFeature(
    Feature.TableFooterColumnVirtualized
  );

  const data = useMemo(() => {
    const shimmeringDivCount = 500;
    return metricsData ?? Array(shimmeringDivCount).fill(null);
  }, [metricsData]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Math.max(1, data.length),
  });

  useEffect(() => {
    if (pagination.pageSize !== data.length) {
      setPagination((prevState) => ({
        ...prevState,
        pageSize: data.length,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, report]);

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting });
  }, [pagination, sorting, onUnmount]);

  const onScroll = useCallback(
    (virtuosoScrollEvent: VirtuosoScrollEvent) => {
      const { element } = virtuosoScrollEvent;
      const { scrollHeight, scrollTop, clientHeight } = element;
      // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
      // TODO change this to constant
      if (
        scrollHeight - scrollTop - clientHeight < 500 &&
        !isFetchingNextPage &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const options: Partial<TableOptions<ReportMetricsResponse | null>> = useMemo(
    () => ({
      data,
      meta: {
        summaryData,
      },
      columns: displayedColumnsConfig,
      state: {
        pagination,
        sorting,
      },
      onPaginationChange: setPagination,
      onSortingChange: (sortingUpdaterFn) => {
        let newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
        if (!newSortVal?.length && report.request.rowGroupings?.length) {
          newSortVal = [
            {
              id: report.request.rowGroupings[0].columnName,
              desc:
                sorting[0]?.id === report.request.rowGroupings[0].columnName
                  ? !sorting[0]?.desc
                  : true,
            },
          ];
        }
        setSorting(newSortVal);
        setActiveSortTableColumn(newSortVal);
        // Persist server-side sorting
        if (newSortVal[0]) {
          updateTempReportConfig({
            ...report,
            request: {
              ...report.request,
              orderBy: newSortVal[0].id,
              orderByAsc: !newSortVal[0].desc,
            },
          } as ReportConfigV2);
        }
      },
      enableRowSelection: true,
    }),
    [
      data,
      summaryData,
      displayedColumnsConfig,
      pagination,
      sorting,
      setActiveSortTableColumn,
      updateTempReportConfig,
      report,
    ]
  );

  const tableProps = useMemo(
    () =>
      ({
        options,
        className,
        tableLayout: 'fixed',
        withOuterPadding: true,
        useVirtuoso: true,
        onVirtuosoTableScroll: onScroll,
        disableColumnVirtualization: !hasTableFooterColumnVirtualizedFeature,
        useStickyHeader: true,
        useStickyFooter: true,
      }) as TableProps<ReportMetricsResponse | null>,
    [options, className, onScroll, hasTableFooterColumnVirtualizedFeature]
  );

  return {
    isReportConfigEmpty,
    tableProps,
    data,
  };
};
